import React, { useState } from 'react';
import moment from 'moment';
import { useDataContext } from '../../context/data.js';
import { formatTheaterPerformanceLabel } from '../../utils/theater';
import {
  isDeliveryRefund,
  woltDeliveryView,
  woltFailureMessageKey,
} from '../../utils/woltDelivery';
import translations from '../../constants/translations.json';
import ProcessingOrder from '../../lottie_processing.js';
import { Modal } from '../ui/Modal.js';
import OrderCancelConfirmationModal from './OrderCancelConfirmationModal.js';
import OrderCancelSuccessModal from './OrderCancelSuccessModal.js';
import { CountdownCircle } from '../ui/CountdownCircle.js';

const ProcessingOrderModal = ({
  isOpen,
  onClose,
  restaurant,
  orderType,
  payConfirm,
  orderStatus,
  cancelOrder,
  isCancelling,
  fromKiosk = false,
  // Theater mode: the performance the order was placed for (null otherwise) —
  // echoed instead of the backend-derived advance-order pickup time.
  theaterPerformance = null,
}) => {
  const [showCancelSuccessModal, setShowCancelSuccessModal] = useState(false);
  // Cancel-confirmation visibility is purely local UI state. This component
  // must NOT call useOrderStatusPolling itself — the owning page already runs
  // that hook and passes its state down; a second instance would start a
  // duplicate checkstatus poller that races the page's one.
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { language } = useDataContext();

  const handleCancelOrder = async () => {
    try {
      // The page's cancelOrder already stops polling, clears the cached
      // order/payConfirm and closes this modal on success.
      const success = await cancelOrder?.();
      if (success) {
        setShowCancelModal(false);
        setShowCancelSuccessModal(true);
      }
    } catch (error) {
      setShowCancelModal(false);
      console.error('error cancelling order', error);
      console.error('error cancelling order', error?.message);
    }
  };

  const accentColor = restaurant?.PrimaryColor || '#96c11f';

  const getStatusConfig = () => {
    if (orderStatus?.isRefunded)
      return {
        emoji: '↩️',
        gradient: 'linear-gradient(135deg, #ef4444, #dc2626)',
        shadow: 'rgba(239,68,68,0.3)',
      };
    if (orderStatus?.isPending)
      return {
        emoji: '⏳',
        gradient: 'linear-gradient(135deg, #f59e0b, #f97316)',
        shadow: 'rgba(245,158,11,0.3)',
      };
    if (orderStatus?.isConfirmed)
      return {
        emoji: '👨‍🍳',
        gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)',
        shadow: 'rgba(59,130,246,0.3)',
      };
    return {
      emoji: '✅',
      gradient: 'linear-gradient(135deg, #22c55e, #16a34a)',
      shadow: 'rgba(34,197,94,0.3)',
    };
  };

  const statusConfig = getStatusConfig();
  // The latest status payload first, then the order as it was placed. A failed
  // or still-unconfirmed delivery has no link or ETA worth showing.
  const woltDelivery = woltDeliveryView(payConfirm?.order, payConfirm);
  const woltDeliveryTrackingUrl = woltDelivery.trackingUrl;
  const woltDeliveryDropoffETA = woltDelivery.eta;
  const woltFailureKey = woltFailureMessageKey(payConfirm?.order, payConfirm);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          if (fromKiosk) {
            onClose();
            return;
          }
          if (!orderStatus?.isPending) onClose();
        }}
        contentClassName="max-w-[440px]"
        noCloseButton={fromKiosk ? false : orderStatus?.isPending}
      >
        <div className="pt-8 px-6 pb-6">
          {/* Status icon */}
          <div className="text-center mb-5">
            {orderStatus?.isPending ? (
              <div className="max-w-[120px] mx-auto mb-4">
                <ProcessingOrder color={accentColor} />
              </div>
            ) : (
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{
                  background: statusConfig.gradient,
                  boxShadow: `0 4px 16px ${statusConfig.shadow}`,
                }}
              >
                <span className="text-4xl">{statusConfig.emoji}</span>
              </div>
            )}

            {fromKiosk ? (
              <>
                <h3 className="text-xl font-bold text-[#1a1a1a] mb-3">
                  {translations[language]?.restaurant_menu_order_thank_you}
                </h3>
                <CountdownCircle className="mb-6" duration={10} color={accentColor} />
              </>
            ) : orderStatus?.isRefunded ? (
              <h3 className="text-xl font-bold text-[#1a1a1a] mb-4">
                {translations[language]?.order_refunded_title || 'Your order was cancelled'}
              </h3>
            ) : orderStatus?.isPending ? (
              <>
                <h3 className="text-xl font-bold text-[#1a1a1a] mb-1.5">
                  {translations[language]?.restaurant_menu_order_received}
                </h3>
                <p className="text-sm text-gray-400 mb-4">
                  {translations[language]?.restaurant_menu_order_message_pending}
                </p>
              </>
            ) : orderStatus?.isConfirmed ? (
              <h3 className="text-xl font-bold text-[#1a1a1a] mb-4">
                {translations[language]?.restaurant_menu_order_message_confirmed}
              </h3>
            ) : (
              <h3 className="text-xl font-bold text-[#1a1a1a] mb-4">
                {translations[language]?.restaurant_menu_order_message_completed}
              </h3>
            )}
          </div>

          {/* Refunded: the courier booking was refused, the guest's money is back */}
          {orderStatus?.isRefunded && (
            <div className="text-center mb-4">
              <p className="text-sm font-semibold text-red-600">
                {/* The delivery wording only when Wolt was the reason; a refund
                    the venue made itself gets the plain one. */}
                {isDeliveryRefund(payConfirm?.order?.reason)
                  ? translations[language]?.order_refunded_message ||
                    'Your delivery could not be completed, so the order was cancelled. You have not been charged – the amount has been refunded to your card.'
                  : translations[language]?.order_refunded_message_generic ||
                    'The order was cancelled and the amount has been refunded to your card.'}
              </p>
            </div>
          )}

          {/* Wolt delivery: failed, awaiting a courier, or on its way */}
          {!orderStatus?.isRefunded && woltDelivery.state === 'failed' && (
            <div className="text-center mb-4">
              <p className="text-sm font-semibold text-red-600">
                {translations[language]?.[woltFailureKey] || translations.en[woltFailureKey]}
              </p>
            </div>
          )}
          {woltDelivery.state === 'pending' && (
            <div className="text-center mb-4">
              <p className="text-sm text-[#555]">
                {translations[language]?.wolt_delivery_pending_message ||
                  'Waiting for a courier to confirm your delivery.'}
              </p>
            </div>
          )}
          {woltDelivery.state === 'confirmed' && woltDeliveryTrackingUrl && (
            <div className="text-center mb-4">
              {woltDeliveryDropoffETA && (
                <p className="text-sm font-semibold text-[#333] mb-1">
                  🕐 {translations[language]?.wolt_delivery_eta_label || 'Forventet levering ca.'}{' '}
                  {new Date(woltDeliveryDropoffETA).toLocaleTimeString('da-DK', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              )}
              <p className="text-sm text-[#555] mb-2">
                {translations[language]?.wolt_delivery_url_message_part_1}
              </p>
              <a
                href={woltDeliveryTrackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block py-2.5 px-6 text-sm font-semibold text-white rounded-[10px] no-underline"
                style={{ background: accentColor }}
              >
                {translations[language]?.wolt_delivery_url_message_part_2}
              </a>
            </div>
          )}

          {/* Order details */}
          <div className="border-t border-gray-100 pt-4 mb-4">
            <div className="flex justify-between mb-1">
              <span className="text-xs text-gray-400">
                {translations[language]?.restaurant_menu_order_id_label}
              </span>
              <span className="text-xs text-[#555] font-mono">{payConfirm?._id}</span>
            </div>
            <div className="flex justify-between mb-1">
              <span className="text-xs text-gray-400">
                {translations[language]?.restaurant_menu_reference_number_label}
              </span>
              <span className="text-xs text-[#555] font-mono">{payConfirm?.refNo}</span>
            </div>
            {restaurant?.TheaterModeEnabled && theaterPerformance ? (
              // Theater mode: echo the chosen performance instead of the
              // backend-derived advance-order pickup time.
              <div className="flex justify-between mb-1">
                <span className="text-xs text-gray-400">
                  {translations[language]?.your_items_theater_row}
                </span>
                <span className="text-xs text-[#555]">
                  {formatTheaterPerformanceLabel(theaterPerformance, language)}
                </span>
              </div>
            ) : (
              payConfirm?.isAdvanceOrder && (
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-gray-400">
                    {orderType === 'delivery'
                      ? translations[language]?.delivery_time
                      : translations[language]?.pickup_time}
                  </span>
                  <span className="text-xs text-[#555]">
                    {moment(payConfirm?.advanceOrderDate).format('DD/MM/YYYY HH:mm')}
                  </span>
                </div>
              )
            )}
          </div>

          {/* Member loyalty discount */}
          {payConfirm?.isLoyaltyDiscounted && Number(payConfirm?.loyaltyDiscount || 0) > 0 && (
            <div className="pt-2 mb-1 flex justify-between items-center">
              <span className="text-sm font-medium" style={{ color: '#2e7d32' }}>
                ⭐ {translations[language]?.loyalty_member_discount || 'Medlemsrabat'}
                {payConfirm?.loyaltyDiscountPercent
                  ? ` (${payConfirm.loyaltyDiscountPercent}%)`
                  : ''}
              </span>
              <span className="text-sm font-semibold" style={{ color: '#2e7d32' }}>
                - {restaurant?.currency?.code || 'DKK'}{' '}
                {Number(payConfirm.loyaltyDiscount).toFixed(2)}
              </span>
            </div>
          )}

          {/* Guest discount code. This is the SERVER's number, echoed back on the
              placed order — the only discount figure that is ever true. Orders
              placed before this feature have no such field, so the row simply
              does not render for them. */}
          {Number(payConfirm?.discountCodeAmount || 0) > 0 && (
            <div className="pt-2 mb-1 flex justify-between items-center">
              <span className="text-sm font-medium" style={{ color: '#2e7d32' }}>
                🏷️ {translations[language]?.discount_code_row || 'Rabatkode'}
                {payConfirm?.discountCode ? ` ${payConfirm.discountCode}` : ''}
              </span>
              <span className="text-sm font-semibold" style={{ color: '#2e7d32' }}>
                - {restaurant?.currency?.code || 'DKK'}{' '}
                {Number(payConfirm.discountCodeAmount).toFixed(2)}
              </span>
            </div>
          )}

          {/* Total */}
          <div className="border-t border-gray-100 pt-3 mb-5 flex justify-between items-center">
            <span className="text-base font-bold text-[#1a1a1a]">
              {translations[language]?.total}:
            </span>
            <span className="text-lg font-bold" style={{ color: accentColor }}>
              {restaurant?.currency?.code || 'DKK'} {payConfirm?.totalAmount?.toFixed(2) || 0}
            </span>
          </div>

          {/* Cancel button */}
          {!restaurant?.IsPoolingEnabled && orderStatus?.isPending && !restaurant?.AutoAccept && (
            <button
              onClick={() => setShowCancelModal(true)}
              disabled={isCancelling}
              className={`w-full py-[14px] text-[15px] font-semibold bg-primary hover:bg-secondary text-white border-none rounded-xl cursor-pointer transition-colors duration-200 ${
                isCancelling ? 'opacity-60' : ''
              }`}
            >
              {translations[language]?.restaurant_menu_cancel_order}
            </button>
          )}
        </div>
      </Modal>

      <OrderCancelConfirmationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onCancelOrder={handleCancelOrder}
        isCancelling={isCancelling}
      />

      <OrderCancelSuccessModal
        isOpen={showCancelSuccessModal}
        onClose={() => setShowCancelSuccessModal(false)}
      />
    </>
  );
};

export default ProcessingOrderModal;
